import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import StatTile from '../ui/StatTile';
import IPSidebar from './IPSidebar';
import IPOverviewTab from './tabs/IPOverviewTab';
import IPMobiliersTab from './tabs/IPMobiliersTab';
import IPPhotosTab from './tabs/IPPhotosTab';
import IPHistoriqueTab from './tabs/IPHistoriqueTab';
import {
  isVisiblePicture,
  getPictureCallId,
  getPictureSrc,
  getPictureCaption,
  formatDateFr,
  formatRelativeDate,
  isOpenCall,
} from './ipHelpers';

const TABS = [
  { id: 'overview', label: "Vue d'ensemble" },
  { id: 'mobiliers', label: 'Mobiliers' },
  { id: 'photos', label: 'Photos' },
  { id: 'historique', label: 'Historique' },
];

const KPI_ICONS = {
  visits: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
  mobiliers: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>,
  open: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  lastVisit: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
};

export default function IPDetail({ ip, fetchWithAuth, onBack }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  // "Open only" filter (toggled from KPI / sidebar)
  const [openOnly, setOpenOnly] = useState(false);

  const [picturesByCallId, setPicturesByCallId] = useState({});
  const [photoModal, setPhotoModal] = useState({ open: false, pictures: [], title: '' });

  const calls = ip.CallIDs;

  // Reset transient state when the store changes
  useEffect(() => {
    setOpenOnly(false);
  }, [ip.Name]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  const assetsCall = useMemo(() => (calls ? calls.find((c) => c.type === 'assets') || null : null), [calls]);

  const allVisitCalls = useMemo(() => (calls ? calls.filter((c) => c.type !== 'assets') : []), [calls]);

  // Historique respects the "open only" filter (column filtering handled by SortableTable)
  const visitCalls = useMemo(
    () => (openOnly ? allVisitCalls.filter(isOpenCall) : allVisitCalls),
    [allVisitCalls, openOnly]
  );

  const latestVisitCall = useMemo(() => {
    const withDate = allVisitCalls.filter((c) => c.date);
    if (withDate.length === 0) return allVisitCalls[0] || null;
    return withDate.reduce((latest, c) => (!latest || new Date(c.date) > new Date(latest.date) ? c : latest), null);
  }, [allVisitCalls]);

  const openCount = useMemo(() => allVisitCalls.filter(isOpenCall).length, [allVisitCalls]);
  const furnituresCount = useMemo(() => {
    if (!assetsCall?.furnitures) return 0;
    return new Set(String(assetsCall.furnitures).split(',').map((f) => f.trim()).filter(Boolean)).size;
  }, [assetsCall]);

  // Fetch pictures for all visits of the store (independent of filters)
  useEffect(() => {
    const callIds = allVisitCalls.map((c) => c.id).filter(Boolean);
    if (!fetchWithAuth || callIds.length === 0) {
      setPicturesByCallId({});
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set('callids', callIds.join(','));
        const res = await fetchWithAuth(`${API_BASE_URL}/api/dwh/one_mag/ips/pictures?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const byId = {};
        if (Array.isArray(data)) {
          data.forEach((pic) => {
            if (!isVisiblePicture(pic)) return;
            const id = getPictureCallId(pic);
            if (!id) return;
            (byId[id] = byId[id] || []).push(pic);
          });
        }
        if (!controller.signal.aborted) setPicturesByCallId(byId);
      } catch (e) {
        if (!controller.signal.aborted) console.error('Error fetching pictures:', e);
      }
    })();
    return () => controller.abort();
  }, [ip.Name, allVisitCalls, fetchWithAuth]);

  const openPhotos = (callId, pictures, title) => setPhotoModal({ open: true, pictures, title });

  const goToHistoriqueOpen = () => {
    setOpenOnly(true);
    handleTabChange('historique');
  };

  const onSelectTicket = () => handleTabChange('historique');

  return (
    <div className="container mx-auto px-4 py-3">
      {/* Compact breadcrumb with inline back */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Recherche
        </button>
        <span className="text-gray-300 dark:text-gray-600">›</span>
        <span className="text-gray-600 dark:text-gray-300 font-medium truncate">{ip.Name}</span>
      </div>

      {/* Title + meta on one compact line */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">{ip.Name}</h1>
        {ip.ShopID && (
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">SAP {ip.ShopID}</span>
        )}
        {ip.Details && <span className="text-sm text-gray-500 dark:text-gray-400">📍 {ip.Details}</span>}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatTile label="Visites" value={allVisitCalls.length} color="blue" icon={KPI_ICONS.visits} />
        <StatTile label="Mobiliers" value={furnituresCount} color="indigo" icon={KPI_ICONS.mobiliers} />
        <StatTile
          label="Interventions ouvertes"
          value={openCount}
          color={openCount > 0 ? 'orange' : 'green'}
          icon={KPI_ICONS.open}
          additionalInfo={openCount > 0 ? 'à traiter' : 'à jour'}
          onClick={openCount > 0 ? goToHistoriqueOpen : null}
        />
        <StatTile
          label="Dernière visite"
          value={latestVisitCall?.date ? formatDateFr(latestVisitCall.date, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          color="teal"
          icon={KPI_ICONS.lastVisit}
          additionalInfo={latestVisitCall?.date ? formatRelativeDate(latestVisitCall.date) : null}
        />
      </div>

      {/* Cockpit layout: main + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow min-w-0">
          {/* Tabs nav */}
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden">
            <nav className="flex -mb-px min-w-max">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.id === 'historique' && openCount > 0 && (
                    <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">{openCount}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="p-4">
            {activeTab === 'overview' && (
              <IPOverviewTab
                ip={ip}
                assetsCall={assetsCall}
                visitCalls={allVisitCalls}
                picturesByCallId={picturesByCallId}
                onOpenPhotos={openPhotos}
                onGoToPhotos={() => handleTabChange('photos')}
              />
            )}
            {activeTab === 'mobiliers' && <IPMobiliersTab calls={calls} />}
            {activeTab === 'photos' && (
              <IPPhotosTab visitCalls={allVisitCalls} picturesByCallId={picturesByCallId} onOpenPhotos={openPhotos} />
            )}
            {activeTab === 'historique' && (
              <IPHistoriqueTab
                visitCalls={visitCalls}
                picturesByCallId={picturesByCallId}
                onOpenPhotos={openPhotos}
                openOnly={openOnly}
                setOpenOnly={setOpenOnly}
                storeName={ip.Name}
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <IPSidebar calls={allVisitCalls} onSelectTicket={onSelectTicket} />
      </div>

      {/* Photo modal */}
      {photoModal.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60" onClick={() => setPhotoModal({ open: false, pictures: [], title: '' })}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{photoModal.title}</h3>
              <button type="button" onClick={() => setPhotoModal({ open: false, pictures: [], title: '' })} className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">✕</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photoModal.pictures.map((pic, idx) => {
                const src = getPictureSrc(pic);
                if (!src) return null;
                const caption = getPictureCaption(pic);
                return (
                  <div key={idx} className="relative">
                    <img src={src} loading="lazy" alt={caption || `Photo ${idx + 1}`} className="w-full h-32 md:h-40 object-cover rounded border border-gray-200 dark:border-gray-700" />
                    {caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-black/60 text-[11px] text-white px-2 py-1 leading-tight line-clamp-2 rounded-b">{caption}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
